import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Copy, Pencil, Plus, Power, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { PageHeader } from '@/components/ui/page-header';
import { useEntreprises } from '@/hooks/queries/useEntreprises';
import {
  useDeletePipeline,
  usePipelines,
  useUpsertPipeline,
} from '@/hooks/queries/useCrmPipelines';
import { useOpportunites } from '@/hooks/queries/useCrmOpportunites';
import type { PipelineRow } from '@/lib/database.types';
import { PipelineWizardDialog } from './PipelineWizardDialog';

export function PipelinesAdminPage(): JSX.Element {
  const { data: entreprises = [] } = useEntreprises();
  const [searchParams] = useSearchParams();
  const queryEntreprise = searchParams.get('entreprise') ?? '';
  const [entrepriseId, setEntrepriseId] = useState(queryEntreprise);
  useEffect(() => {
    if (entrepriseId) return;
    if (queryEntreprise && entreprises.some((e) => e.id === queryEntreprise)) {
      setEntrepriseId(queryEntreprise);
    } else if (entreprises[0]) {
      setEntrepriseId(entreprises[0].id);
    }
  }, [entreprises, entrepriseId, queryEntreprise]);

  const { data: pipelines = [] } = usePipelines(entrepriseId);
  const upsert = useUpsertPipeline();
  const remove = useDeletePipeline();

  const [wizardOpen, setWizardOpen] = useState(false);
  const [editing, setEditing] = useState<PipelineRow | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<PipelineRow | null>(null);

  const handleToggleActif = async (p: PipelineRow): Promise<void> => {
    await upsert.mutateAsync({
      id: p.id,
      entreprise_id: p.entreprise_id,
      activite_id: p.activite_id,
      nom: p.nom,
      actif: !p.actif,
    });
    toast.success(p.actif ? 'Pipeline désactivé' : 'Pipeline réactivé');
  };

  return (
    <div className="space-y-4 p-6">
      <PageHeader
        title="Pipelines CRM"
        subtitle="Configuration no-code : étapes, champs custom, motifs de perte. Aucune ligne de code requise."
        actions={
          <div className="flex items-center gap-2">
            <select
              value={entrepriseId}
              onChange={(e) => setEntrepriseId(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            >
              {entreprises.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.nom}
                </option>
              ))}
            </select>
            <Button
              onClick={() => {
                setEditing(null);
                setWizardOpen(true);
              }}
            >
              <Plus className="h-4 w-4" /> Nouveau pipeline
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
        {pipelines.map((p) => (
          <PipelineCard
            key={p.id}
            pipeline={p}
            onEdit={() => {
              setEditing(p);
              setWizardOpen(true);
            }}
            onToggle={() => void handleToggleActif(p)}
            onDelete={() => setConfirmDelete(p)}
            onDuplicate={async () => {
              const dup = await upsert.mutateAsync({
                entreprise_id: p.entreprise_id,
                activite_id: p.activite_id,
                nom: `${p.nom} (copie)`,
                description: p.description,
                couleur: p.couleur,
                icone: p.icone,
                ordre: p.ordre + 1,
              });
              toast.success(`Pipeline dupliqué : ${dup.nom}`);
            }}
          />
        ))}
        {pipelines.length === 0 && (
          <div className="col-span-full rounded-lg border border-dashed border-border bg-card p-8 text-center text-sm text-muted-foreground">
            Aucun pipeline configuré.{' '}
            <button
              type="button"
              className="text-primary hover:underline"
              onClick={() => {
                setEditing(null);
                setWizardOpen(true);
              }}
            >
              Créez le premier
            </button>
            .
          </div>
        )}
      </div>

      <PipelineWizardDialog
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        entrepriseId={entrepriseId}
        editing={editing}
      />

      <ConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        destructive
        title={`Supprimer le pipeline « ${confirmDelete?.nom} » ?`}
        description="La suppression échoue si des opportunités sont liées. Désactivez plutôt si besoin."
        loading={remove.isPending}
        onConfirm={async () => {
          if (!confirmDelete) return;
          try {
            await remove.mutateAsync(confirmDelete.id);
            toast.success('Pipeline supprimé');
          } catch (e) {
            toast.error((e as Error).message);
          }
          setConfirmDelete(null);
        }}
      />
    </div>
  );
}

function PipelineCard({
  pipeline,
  onEdit,
  onToggle,
  onDelete,
  onDuplicate,
}: {
  pipeline: PipelineRow;
  onEdit: () => void;
  onToggle: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
}): JSX.Element {
  const { data } = useOpportunites({
    entrepriseId: pipeline.entreprise_id,
    pipelineId: pipeline.id,
    statut: 'open',
    pageSize: 1,
  });
  const nbOpen = data?.count ?? 0;

  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="mb-2 flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="h-3 w-3 rounded-full" style={{ background: pipeline.couleur }} />
            <h3 className="line-clamp-1 text-sm font-semibold">{pipeline.nom}</h3>
            {!pipeline.actif && <Badge variant="secondary">Inactif</Badge>}
          </div>
          {pipeline.description && (
            <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
              {pipeline.description}
            </p>
          )}
        </div>
      </div>
      <div className="mb-3 flex items-center gap-3 text-[11px] text-muted-foreground">
        <span>
          <strong className="text-foreground">{nbOpen}</strong> opportunités ouvertes
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-1">
        <Button variant="outline" size="sm" onClick={onEdit}>
          <Pencil className="h-3 w-3" /> Modifier
        </Button>
        <Button variant="outline" size="sm" onClick={onDuplicate}>
          <Copy className="h-3 w-3" /> Dupliquer
        </Button>
        <Button variant="outline" size="sm" onClick={onToggle}>
          <Power className="h-3 w-3" />
          {pipeline.actif ? 'Désactiver' : 'Activer'}
        </Button>
        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={onDelete}>
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}
